import type { FC } from 'react';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Stack, Typography } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import useGuidedTour from 'src/components/app/context/GuidedTourContext';
import TranslationToggle from 'src/components/common/TranslationToggle';
import DashboardCommentsProvider from 'src/components/dashboard/DashboardContext/CommentsContext';
import { useTagCloud } from 'src/components/dashboard/DashboardContext/TagCloudContext';
import TagCloud from 'src/components/dashboard/DashboardPage/widgets/TagCloudWidget/TagCloud';
import TagCloudDropdown from 'src/components/dashboard/DashboardPage/widgets/TagCloudWidget/TagCloudDropdown';
import TagCloudSearch from 'src/components/dashboard/DashboardPage/widgets/TagCloudWidget/TagCloudSearch';

import { styles } from './index.styles';

export const TAG_CLOUD_WIDGET_TEST_ID = 'tag-cloud-widget';

const TagCloudWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { t } = useTranslation('dashboard');
  const tagCloudWidgetRef = useRef<HTMLElement>(null);
  const tagCloudRef = useRef(null);
  const { toggleTranslation, translated } = useTagCloud();
  const { createStep } = useGuidedTour();
  const guidedTour = useMemo(
    () => createStep(config.id, config.guided_tour_step),
    [config.guided_tour_step],
  );

  return (
    <Box
      ref={tagCloudWidgetRef}
      sx={{
        p: 2.5,
      }}
    >
      <DashboardCommentsProvider source="token">
        <Grid container data-test-id={TAG_CLOUD_WIDGET_TEST_ID} spacing={2}>
          <Grid
            item
            xs={12}
            sx={{
              pb: 3,
            }}
          >
            <Stack
              direction="row"
              spacing={1.25}
              sx={{
                alignItems: 'center',
              }}
            >
              {guidedTour}
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {config.name ? config.name : t('TagCloud.TagCloud')}
              </Typography>
              <Box>
                <TranslationToggle
                  isDefaultLanguage={translated}
                  onToggle={toggleTranslation}
                />
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sx={styles.tagCloudFilterGroup}>
            <TagCloudSearch />
            <TagCloudDropdown />
          </Grid>
          <Grid item xs={12}>
            <Box ref={tagCloudRef}>
              <TagCloud
                questions={config.questions}
                tagCloudRef={tagCloudRef}
              />
            </Box>
          </Grid>
        </Grid>
      </DashboardCommentsProvider>
    </Box>
  );
};

export default TagCloudWidget;
